import { Alert, AppLayout, Badge, Button, ExpandableSection, Icon, Modal, Popover } from 'AWS-UI-Components-React';
import React from 'react';
import {
  ComponentNames,
  PCOGS_URL,
  SECONDARY_COMPONENT_URL,
  expiryDaysSelfServiceCostCorrectionTool,
} from '../../constants/spotlight_elements';
import ColumnLayout from '@amzn/awsui-components-react-v3/polaris/column-layout';
import { NavigationHeaderStyle } from '../../styles/common_styles.jsx';
import logger from '../../utils/logging';

import '../../styles/global.css';
import '../../styles/spotlight.css';
import { toTitleCase } from '../../utilities';
import { getJSClient } from '../js_client';
import { getSelfServiceClient } from './spotcp-self-service-client';
import { SpotlightAttributes } from './spotlight_attributes';
import { InputField } from '../common/input_field';
import { Container } from '@amzn/awsui-components-react-v3';
import { AccuracyButton } from '../buttons/accuracy_button';
import { OPERATION_STATUS } from '../reports/common/constants';
import { SelfServiceAlert } from './self-service-alert';

const defaultDenylistPayload = {
  iog: '',
  currency: '',
  tt: '',
  approverAlias: '',
  minCost: '',
  maxCost: '',
  daysToExpire: 7,
};

const defaultAllowlistPayload = {
  approverAlias: '',
  tt: '',
  vendorCode: '',
  daysToExpire: 7,
};

const PCOGS_INVENTORY = 'PCOGS_INVENTORY';
const PCOGS_DROPSHIP_VENDOR = 'PCOGS_DROPSHIP_VENDOR';
const DENYLIST_COST_DELTA = 1.2;
const COST = 'cost';
const REVENUE = 'revenue';

export class Spotlight extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      formSubmitted: false,
      spotlightError: false,
      loading: false,
      error: null,
      copyReady: false,
      parametersPayload: {},
      pcogsSubTracerId: '',
      secondaryComponentSubTracerId: '',
      response: null,
      lastTrace: '',
      showDenyListModal: false,
      denylistPayload: Object.assign({}, defaultDenylistPayload),
      accessDenylistButton: true,
      denylistLoading: false,
      denylistResponseState: OPERATION_STATUS.NONE,
      denylistErrorMessage: '',
      ttValidation: '',
      approverAliasValidation: '',
      costValidation: '',
      mincostValidation: '',
      maxcostValidation: '',
      expiryDaysDenylist: 7,
      showAllowlistModal: false,
      allowlistPayload: Object.assign({}, defaultAllowlistPayload),
      accessAllowlistButton: true,
      allowlistLoading: false,
      allowlistResponseState: OPERATION_STATUS.NONE,
      vendorCodeValidation: '',
      expiryDaysAllowlist: 7,
      allowlistErrorMessage: '',
    };
    this.payload = {};
    this.rootSite = window.location.href.split('#')[0];
  }

  componentDidMount = () => {
    this.client = getJSClient();
    this.initialClientTasks();
    document.title = 'Phoenix : SpotLight';
  };

  initialClientTasks = () => {
    this.selfServiceClient = getSelfServiceClient();
  };

  resetTable = () => {
    this.setState({formSubmitted: false, response: null, copyReady: false});
  };

  submitForm = () => {
    this.setState({formSubmitted: true, loading: true, response: null, copyReady: false});
  };

  onFailure = (err) => {
    logger.error('Spotlight request failure', err);
    this.setState({formSubmitted: false, spotlightError: true, loading: false, error: err});
  };

  onSuccess = (response) => {
    console.log(response);
    this.state.response = response;
    this.setState({spotlightError: false, loading: false, error: null});
    if (response.tracer) {
      this.state.pcogsSubTracerId = response.tracer.subTracers.find(
        subTracer => subTracer.summary === 'Cost found.' || subTracer.summary === 'No cost.').id;
      this.state.secondaryComponentSubTracerId = (response.tracer.subTracers.find(subTracer => subTracer.summary
        === 'Secondary Components found.') || {id: this.state.secondaryComponentSubTracerId}).id;
      this.handlePcogsSubTracerIdChange(this.state.pcogsSubTracerId);
      this.handleSecondaryComponentSubTracerIdChange(this.state.secondaryComponentSubTracerId);
    }
  };
  getCallbackPayload = (payload) => {
    this.setState({formSubmitted: true, loading: true, parametersPayload: payload});
    this.client.getSpotCP(this.onSuccess, this.onFailure, payload);
  };

  isAvailable(id) {
    if (!this.state.response || !this.state.response.tracer) {
      return false;
    }

    const index = this.state.response.tracer.subTracers.findIndex(subTracer => subTracer.id === id);
    return (index !== -1);
  };

  handlePcogsSubTracerIdChange = (id) => {
    this.setState({pcogsSubTracerId: id});
  };

  handleSecondaryComponentSubTracerIdChange = (id) => {
    this.setState({secondaryComponentSubTracerId: id});
  };

  getSubTracerSummary = (id) => {
    const subTracer = this.getSubTracer(id);

    if (!subTracer) {
      return 'Skipping this Cascade.';
    }

    if (!subTracer.summary) {
      return 'Cost not find.';
    }

    return subTracer.summary;
  };

  getSubTracerName = (id) => {
    const subTracer = this.getSubTracer(id);
    const subTracerNames = {
      'PCOGS_SBA': 'SBA',
      'PCOGS_MOD': 'MOD',
      'PCOGS_ADVANTAGE': 'ADVANTAGE',
      'PCOGS_INVENTORY': 'INVENTORY',
      'PCOGS_OPEN_PO': 'OPEN PO',
      'PCOGS_RECENT_SHIPMENTS': 'RECENT SHIPMENTS',
      'PCOGS_DIRECT_VENDOR': 'DIRECT VENDOR',
      'PCOGS_DROPSHIP_VENDOR': 'DROPSHIP VENDOR',
      'PCOGS_ALL_VENDOR': 'ALL VENDOR',
      'PCOGS_NULL': 'NULL',
      'SEC_COMPONENTS': 'SECONDARY COMPONENTS',
      'SEC_PRODUCT_COST_BUCKET': 'PRODUCT COST BUCKET',
      'SEC_WEIGHT_BUCKET': 'WEIGHT BUCKET',
    };

    if (!subTracer) {
      return subTracerNames[id];
    }

    return subTracerNames[subTracer.name];
  };

  getSubTracer = (id) => {
    if (!this.state.response || !this.state.response.tracer) {
      return null;
    }

    const index = this.state.response.tracer.subTracers.findIndex(subTracer => subTracer.id === id);
    if (index === -1) {
      return null;
    }

    return this.state.response.tracer.subTracers[index];
  };

  getStatus = (summary) => {
    if (summary === 'Cost found.') {
      return 'found';
    } else if (summary === 'Cost not find.') {
      return 'notFound';
    } else if (summary === 'No cost.') {
      return 'noCost';
    } else if (summary === 'Secondary Components found.') {
      return 'secFound';
    } else if (summary === 'Secondary Components not found.') {
      return 'secNotFound';
    }
    return 'skip';
  };

  getTotal = (type) => {
    return this.state.response.spotCPComponents
               .filter(component => component.financialType === type)
               .map(a => parseFloat(a.monetaryValue.split(' ')[1]))
               .reduce((a, b) => a + b, 0)
               .toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
  };

  /*
   getComponentArray = (type) => {
   let componentList = [];
   this.state.response.spotCPComponents.forEach(c => {
   let componentEntry = {};
   componentEntry.name = c.name;
   componentEntry.value = parseFloat(c.monetaryValue.split(' ')[1]);
   componentEntry.type = c.financialType;
   componentList.push(componentEntry);
   })

   console.log(componentList);

   return componentList.filter(component => component.type === type).map(c => c);
   };
   */

  formatMonetaryValue = (value) => {
    if (!isNaN(parseFloat(value.split(' ')[0]))) {
      return value.split(' ')[1] + ' ' + this.formatValue(value.split(' ')[0]);
    } else if (!isNaN(parseFloat(value.split(' ')[1]))) {
      return value.split(' ')[0] + ' ' + this.formatValue(value.split(' ')[1]);
    }
    return value;
  };

  // Format displayed negative numbers from '-XX' to '(XX)' in SpotLight.
  formatValue = (value) => {
    const val = parseFloat(value).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
    return val < 0 ? '(' + (-1 * val) + ')' : val;
  };

  formatComponentName = (name) => {
    if (ComponentNames.hasOwnProperty(name)) {
      return ComponentNames[name];
    } else if (name.includes('_')) {
      return toTitleCase(name.replace(/_/g, ' '));
    } else {
      return name;
    }
  };

  shareReport = () => {
    console.log(`Copy URL`);
    const url = `${ this.rootSite }#/spotlight/${ this.state.parametersPayload.channelType }&${ this.state.parametersPayload.marketplaceId }&${ this.state.parametersPayload.merchantCustomerId }&${ this.state.parametersPayload.asin }`;
    if (this.state.formSubmitted) {
      if (navigator.clipboard) {
        // If support Clipboard API
        navigator.clipboard.writeText(url).then();
      } else if (window.clipboardData) {
        // If browser is IE
        window.clipboardData.setData('Text', url);
      } else {
        // Use react hooks if above operation not supported by browser
        document.querySelector(url).select();
        document.execCommand('copy');
      }
      this.setState({copyReady: true});
    } else {
      this.setState({copyReady: false});
    }
  };

  content = () => {
    const message = !this.state.copyReady ? (
      <span className="awsui-util-status-negative">
        <Icon variant="error" name="status-negative" /> { 'Please load report' }
      </span>
    ) : (
                      <span className="awsui-util-status-positive">
        <Icon variant="success" name="status-positive" /> { 'URL Copied' }
      </span>);

    return (
      <div className="awsui-util-p-s">
        <div className="custom-home__header custom-awsui-util-pt-xxxl awsui-row">
          <div className="col-xxs-12">
            <div className="awsui-row">
              <div className="custom-home__header-title col-xxs-10 offset-xxs-1 col-s-6 col-l-5 offset-l-2 col-xl-6">
                <div className="awsui-row">
                  <div className="awsui-util-container">

                    <div className="awsui-util-container-header">
                      <div className="awsui-util-action-stripe">
                        <div className="awsui-util-action-stripe-title">
                          <div className="awsui-util-font-size-2">
                            Spot CP Summary
                          </div>
                        </div>
                        <div className="awsui-util-action-stripe-group">
                          <Popover
                            size="small"
                            position={ 'top' }
                            triggerType="custom"
                            dismissButton={ false }
                            content={ message }
                          >
                            <Button
                              text="Share Report URL"
                              variant="normal"
                              disabled={ !this.state.formSubmitted }
                              loading={ this.state.loading }
                              icon="copy"
                              onClick={ this.shareReport }
                            />
                          </Popover>
                        </div>
                      </div>
                    </div>

                    <div className="transparency-container">
                      <div>{ this.renderErrorTexts() }</div>
                      <div>{ this.renderSpotCPComponents() }</div>
                      <div>{ this.renderProductCostCascade() }</div>
                      <div>{ this.renderSecondaryComponentCascade() }</div>
                      <div>{ this.renderContextComponent() }</div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderErrorTexts = () => {
    if (!this.state.response) {
      if (!this.state.formSubmitted && !this.state.spotlightError) {
        return (<Alert header="No Reports to Show" content="Create a report using the sidebar to begin." />);
      } else if (this.state.loading) {
        return (<Alert type="success" header="Report Calculating" content="Getting Spot CP Details..." />);
      } else if (this.state.spotlightError) {
        return (<Alert type="warning" header="Request Timeout" content="Could not hear from Spot CP Service. Please retry the request.
        If persist please submit a ticket" />);
      } else {
        return (<Alert type="warning" header="Network Issue" content="Could not reach Spot CP Service. Please submit a ticket." />);
      }
    }
    if (this.state.response.errorMessage) {
      return (
        <Alert type="error"
               header={ this.state.response.errorMessage.message }
               content={
                 <div>
                   <p>{ this.state.response.errorMessage.exception } </p>
                   <p>{ this.state.response.errorMessage.suggestion } </p>
                 </div>
               }
        />);
    }
    return (<div />);
  };

  renderSpotCPComponents = () => {
    if (!this.state.response || this.state.response.errorMessage) {
      return <div />;
    }

    return (<ExpandableSection header="Component Summary" variant="container">
        <div style={ {border: '1px solid lightgray', padding: '5px'} }>
          <table className="info-table">
            <tbody>
            <tr>
              <th className="boldTitle">Total Cost: { this.getTotal(COST) }</th>
              <th className="boldTitle">Total Revenue: { this.getTotal(REVENUE) }</th>
            </tr>
            <tr style={ {verticalAlign: 'top'} }>
              <td>
                <table className="info-table">
                  <tbody>
                  { this.state.response.spotCPComponents.filter(component => component.financialType === COST)
                        .sort((a, b) => this.formatComponentName(a.name) <= this.formatComponentName(b.name) ? -1 : 1)
                        .map((component, index) => (
                          <tr key={ index }>
                            <th>{ this.formatComponentName(component.name) }</th>
                            <td>{ this.formatMonetaryValue(component.monetaryValue) }</td>
                          </tr>
                        )) }
                  </tbody>
                </table>
              </td>

              <td>
                <table className="info-table">
                  <tbody>
                  { this.state.response.spotCPComponents.filter(component => component.financialType === REVENUE)
                        .sort((a, b) => this.formatComponentName(a.name) <= this.formatComponentName(b.name) ? -1 : 1)
                        .map((component, index) => (
                          <tr key={ index }>
                            <th>{ this.formatComponentName(component.name) }</th>
                            <td>{ this.formatMonetaryValue(component.monetaryValue) }</td>
                          </tr>
                        )) }
                  </tbody>
                </table>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </ExpandableSection>
    );
  };

  renderProductCostCascade = () => {
    if (!this.state.response || this.state.response.errorMessage) {
      return <div />;
    }

    const pcogsSubTracerIds = ['PCOGS_SBA', 'PCOGS_MOD', 'PCOGS_ADVANTAGE', 'PCOGS_INVENTORY',
                               'PCOGS_OPEN_PO', 'PCOGS_RECENT_SHIPMENTS', 'PCOGS_DIRECT_VENDOR', 'PCOGS_DROPSHIP_VENDOR',
                               'PCOGS_ALL_VENDOR', 'PCOGS_NULL'];

    return (<ExpandableSection
      header={
        <div>
          Product Cost Cascade and Contra-Cogs Details &nbsp;
          <a target="_blank" href={ PCOGS_URL }>
            <Icon variant="link" name="status-info" />
          </a>
        </div>
      }
      variant="container"
      expanded={ true }
    >
      <div style={ {border: '1px solid lightgray', padding: '5px'} }>
        <ul className="spotcascade-container">
          { pcogsSubTracerIds.map((id) => (
            <span key={ id }>
              <li className="spotcascade-arrow" />
              { this.renderCascadeBox(id, this.state.pcogsSubTracerId === id, this.handlePcogsSubTracerIdChange) }
            </span>
          )) }
        </ul>

        <div className="tracer-contents">
          { this.renderTracer(this.state.pcogsSubTracerId) }
        </div>
      </div>
    </ExpandableSection>);
  };

  renderSecondaryComponentCascade = () => {
    if (!this.state.response || this.state.response.errorMessage) {
      return <div />;
    }

    return (<ExpandableSection
      header={
        <div>
          Secondary Component Details &nbsp;
          <a target="_blank" href={ SECONDARY_COMPONENT_URL }>
            <Icon variant="link" name="status-info" />
          </a>
        </div>
      }
      variant="container"
    >
      <div style={ {border: '1px solid lightgray', padding: '5px'} }>
        <ul className="spotcascade-container">
          <li className="spotcascade-arrow" />
          { this.renderCascadeBox('SEC_COMPONENTS', this.state.secondaryComponentSubTracerId
            === 'SEC_COMPONENTS', this.handleSecondaryComponentSubTracerIdChange) }
          <li className="spotcascade-arrow" />
          <span style={ {paddingLeft: '5px'} }>
            { this.renderCascadeBox('SEC_PRODUCT_COST_BUCKET', this.state.secondaryComponentSubTracerId
              === 'SEC_PRODUCT_COST_BUCKET', this.handleSecondaryComponentSubTracerIdChange) }
            <span style={ {padding: '10px'} }> <Icon name="add-plus" /> </span>
            { this.renderCascadeBox('SEC_WEIGHT_BUCKET', this.state.secondaryComponentSubTracerId
              === 'SEC_WEIGHT_BUCKET', this.handleSecondaryComponentSubTracerIdChange) }
          </span>
        </ul>

        <div className="tracer-contents">
          { this.renderTracer(this.state.secondaryComponentSubTracerId) }
        </div>
      </div>
    </ExpandableSection>);
  };

  renderContextComponent = () => {
    if (!this.state.response || this.state.response.errorMessage) {
      return <div />;
    }

    return (<ExpandableSection header="ASIN Attributes" variant="container">
      <div style={ {border: '1px solid lightgray', padding: '5px'} }>
        <table className="info-table">
          <tbody>
          { this.state.response.contextComponents.map((contextComponent, index) => (
            <tr key={ index }>
              <th>{ contextComponent.name }</th>
              <td>{ contextComponent.value }</td>
            </tr>
          )) }
          </tbody>
        </table>
      </div>
    </ExpandableSection>);
  };

  renderCascadeBox(id, isSelected, onClick: (id) => void) {
    return (
      <li className={ `spotcascade${ this.isAvailable(id) ? ' spotcascade-available' : '' }${ isSelected ? ' spotcascade-active' : '' }` }
          onClick={ () => onClick(id) }>
        <div style={ {fontWeight: 1000, fontSize: '11px'} }> { this.getSubTracerName(id) } </div>
        <div className="spotcascade-info">
          <p data-status={ this.getStatus(this.getSubTracerSummary(id)) } className="status">
          </p>
        </div>
      </li>
    );
  };

  renderTracer(id) {
    if (!id) {
      return <div />;
    }

    const tracer = this.getSubTracer(id);
    if (!tracer) {
      return <div />;
    }

    if (tracer.trace.length === 1) {
      this.state.lastTrace = '';
    }

    return (<div>
        { tracer.trace.map((info, index) => (
          <div className="trace" key={ index }>
            { this.renderTrace(info) }
          </div>
        )) }
        { (this.state.pcogsSubTracerId === PCOGS_INVENTORY && id === PCOGS_INVENTORY) ? <Button
          text="DenyList Inventory Cost"
          variant="primary"
          onClick={ () => this.setState({showDenyListModal: true}) }
        /> : '' }

        <Modal visible={ this.state.showDenyListModal }
               expandToFit={ true } header="DenyList Inventory Cost" onDismiss={ () => this.setState({
                                                                                                       showDenyListModal: false,
                                                                                                       denylistPayload: defaultDenylistPayload,
                                                                                                       denylistResponseState: OPERATION_STATUS.NONE,
                                                                                                     }) } footer={
          <span className="awsui-util-f-r">
            <Button text="Deny List" variant="primary" onClick={ this.addDenyList }
                    loading={ this.state.denylistLoading }
                    disabled={ this.denylistButtonDisabled() }
            />
          </span>
        }>
          <Alert header="Note Prior to using the tool" content="Please secure L7 approval and provide details in mandatory fields below.  Corrections are valid for specified time period only.  Please work with source teams for a permanent solution to data issues.
        The Maximum cost to be denylisted cannot be more than 20% of Minimum cost denylisted." />
          <div>
            { this.getDenyList() }
            { this.renderDenyListSuccess() }
          </div>
        </Modal>

        { (this.state.pcogsSubTracerId === PCOGS_DROPSHIP_VENDOR && id === PCOGS_DROPSHIP_VENDOR) ? <Button
          text="AllowList Vendor Code"
          variant="primary"
          onClick={ () => this.setState({showAllowlistModal: true}) }
        /> : '' }

        <Modal visible={ this.state.showAllowlistModal }
               expandToFit={ true } header="AllowList Vendor Code" onDismiss={ () => this.setState({
                                                                                                         showAllowlistModal: false,
                                                                                                         allowlistPayload: defaultAllowlistPayload,
                                                                                                         allowlistResponseState: OPERATION_STATUS.NONE,
                                                                                                       }) } footer={
          <span className="awsui-util-f-r">
            <Button text="Allow List" variant="primary" onClick={ this.addAllowlist }
                    loading={ this.state.allowlistLoading }
                    disabled={ this.allowlistButtonDisabled() }
            />
          </span>
        }>
          <Alert header="Note Prior to using the tool"
                 content="Please secure L7 approval and provide details in mandatory fields below.  Corrections are valid for specified time period only.  Please work with source teams for a permanent solution to data issues." />
          <div>
            { this.getAllowlist() }
            { this.renderAllowlistSuccess() }
          </div>
        </Modal>
      </div>
    );
  };

  getDenyList = () => {
    return (
      <Container>
        <ColumnLayout columns={ 2 } borders="all">
          <div>
            <InputField text="ASIN"
                        placeholder={ this.state.parametersPayload.asin }
                        value={ this.state.parametersPayload.asin }
                        disabled={ true }
                        required={ true }
            />
          </div>
          <div>
            <InputField text="Marketplace ID"
                        placeholder={ this.state.parametersPayload.marketplaceId }
                        value={ this.state.parametersPayload.marketplaceId }
                        disabled={ true }
                        required={ true }
            />
          </div>
          <div>
            <InputField text="Merchant Customer ID"
                        placeholder={ this.state.parametersPayload.merchantCustomerId }
                        value={ this.state.parametersPayload.merchantCustomerId }
                        disabled={ true }
                        required={ true }
            />
          </div>
          <div>
            <InputField text="Inventory Owner Group (IOG)"
                        placeholder={ 'Enter IOG' }
                        value={ this.state.denylistPayload.iog }
                        disabled={ false }
                        required={ true }
                        onChange={ this.setIOG }
            />
          </div>
          <div>
            <InputField text="Currency"
                        placeholder={ 'Enter Currency Code' }
                        value={ this.state.denylistPayload.currency }
                        disabled={ false }
                        required={ true }
                        onChange={ this.setCurrency }
            />
          </div>
          <div>
            <InputField text="TT"
                        placeholder={ 'Enter TT Link' }
                        value={ this.state.denylistPayload.tt }
                        disabled={ false }
                        required={ true }
                        onChange={ this.setDenylistTT }
            />
          </div>
          <div>
            <InputField text="User Alias"
                        placeholder={ this.props.identity.username }
                        value={ this.props.identity.username }
                        disabled={ true }
                        required={ true }
            />
          </div>
          <div>
            <InputField text="L7 Approver Alias"
                        placeholder={ 'Enter Approver Alias' }
                        value={ this.state.denylistPayload.approverAlias }
                        disabled={ false }
                        required={ true }
                        onChange={ this.setDenylistApproverAlias }
            />
          </div>
          <div>
            <InputField text="Minimum Cost "
                        placeholder={ 'Enter Minimum Cost' }
                        value={ this.state.denylistPayload.minCost }
                        disabled={ false }
                        required={ true }
                        onChange={ this.setMinCost }
            />
          </div>
          <div>
            <InputField text="Maximum Cost"
                        placeholder={ 'Enter Maximum Cost' }
                        value={ this.state.denylistPayload.maxCost }
                        disabled={ false }
                        required={ true }
                        onChange={ this.setMaxCost }
            />
          </div>
        </ColumnLayout>

        <AccuracyButton
          text="Days to Expire Record"
          items={ expiryDaysSelfServiceCostCorrectionTool }
          onChange={ this.setExpirationDaysForDenylist }
          selected={ this.state.denylistPayload.daysToExpire }
          required={ true }
        />
      </Container>
    );
  };

  getAllowlist = () => {
    return (
      <Container>
        <ColumnLayout columns={ 2 } borders="all">
          <div>
            <InputField
              text="ASIN"
              placeholder={ this.state.parametersPayload.asin }
              value={ this.state.parametersPayload.asin }
              disabled={ true }
              required={ true }
            />
          </div>
          <div>
            <InputField
              text="Marketplace ID"
              placeholder={ this.state.parametersPayload.marketplaceId }
              value={ this.state.parametersPayload.marketplaceId }
              disabled={ true }
              required={ true }
            />
          </div>
          <div>
            <InputField
              text="Merchant Customer ID"
              placeholder={ this.state.parametersPayload.merchantCustomerId }
              value={ this.state.parametersPayload.merchantCustomerId }
              disabled={ true }
              required={ true }
            />
          </div>
          <div>
            <InputField
              text="TT"
              placeholder={ 'Enter TT Link' }
              value={ this.state.allowlistPayload.tt }
              disabled={ false }
              required={ true }
              onChange={ this.setAllowlistTT }
            />
          </div>
          <div>
            <InputField
              text="User Alias"
              placeholder={ this.props.identity.username }
              value={ this.props.identity.username }
              disabled={ true }
              required={ true }
            />
          </div>
          <div>
            <InputField
              text="Approver Alias"
              placeholder={ 'Enter Approver Alias' }
              value={ this.state.allowlistPayload.approverAlias }
              disabled={ false }
              required={ true }
              onChange={ this.setAllowlistPayload }
            />
          </div>
        </ColumnLayout>
        <div>
          <InputField
            text="Vendor Code"
            placeholder={ 'Enter Vendor Code' }
            value={ this.state.allowlistPayload.vendorCode }
            disabled={ false }
            required={ true }
            onChange={ this.setVendorCode }
          />
        </div>
        <AccuracyButton
          text="Days to Expire Record"
          items={ expiryDaysSelfServiceCostCorrectionTool }
          onChange={ this.setExpirationDaysForAllowlist }
          selected={ this.state.allowlistPayload.daysToExpire }
          required={ true }
        />
      </Container>
    );
  };

  addDenyList = () => {
    const min = parseFloat(this.state.denylistPayload.minCost);
    const max = parseFloat(this.state.denylistPayload.maxCost);
    this.setState({expiryDays: this.state.denylistPayload.daysToExpire});
    if (min * DENYLIST_COST_DELTA < max || max < min) {
      this.setState({costValidation: 'Please enter valid Min Cost & Max Cost as per requirements. The Maximum cost cannot be more than 20% of Minimum cost.'});
    } else {
      this.setState({denylistLoading: true});
      const newPayload = Object.assign({}, this.state.denylistPayload);
      newPayload.asin = this.state.parametersPayload.asin;
      newPayload.marketplaceId = this.state.parametersPayload.marketplaceId;
      newPayload.merchantCustomerId = this.state.parametersPayload.merchantCustomerId;
      newPayload.userAlias = this.props.identity.username;
      this.setState({denylistPayload: newPayload});
      this.selfServiceClient.postDenyList(this.OnDenylistSuccess, this.OnDenylistSuccess, this.state.denylistPayload);
    }
  };

  setExpirationDaysForDenylist = (chosen) => {
    const newPayload = Object.assign({}, this.state.denylistPayload);
    newPayload.daysToExpire = chosen.detail.selectedOption.value;
    this.state.expiryDaysDenylist = chosen.detail.selectedOption.value;
    this.setState({denylistPayload: newPayload});
  };

  setIOG = (input) => {
    const newPayload = Object.assign({}, this.state.denylistPayload);
    newPayload.iog = input.detail.value;
    this.setState({denylistPayload: newPayload});
  };

  setCurrency = (input) => {
    const newPayload = Object.assign({}, this.state.denylistPayload);
    newPayload.currency = input.detail.value;
    this.setState({denylistPayload: newPayload});
  };

  checkTT = (input) => {
    if (input != '' && !input.match(/^https:\/\/t.corp.amazon.com/)) {
      this.setState({ttValidation: 'Please enter valid TT Link like "https://t.corp.amazon.com/ABC123".'});
      return false;
    } else {
      this.setState({ttValidation: ''});
      return true;
    }
  };

  setDenylistTT = (input) => {
    const newPayload = Object.assign({}, this.state.denylistPayload);
    const tt = input.detail.value;
    if (this.checkTT(tt)) {
      newPayload.tt = input.detail.value;
      this.setState({denylistPayload: newPayload});
    }
  };

  checkApproverAlias = (input) => {
    if (input != '' && !input.match(/^[a-z]/)) {
      this.setState({approverAliasValidation: 'Please enter valid Approver Alias.'});
      return false;
    } else {
      this.setState({approverAliasValidation: ''});
      return true;
    }
  };

  setDenylistApproverAlias = (input) => {
    const newPayload = Object.assign({}, this.state.denylistPayload);
    const appAlias = input.detail.value;
    if (this.checkApproverAlias(appAlias)) {
      newPayload.approverAlias = input.detail.value;
      this.setState({denylistPayload: newPayload});
    }
  };

  setMinCost = (input) => {
    const newPayload = Object.assign({}, this.state.denylistPayload);
    const min = input.detail.value;
    if (min && !min.match(/^\d+\.\d{0,2}$/)) {
      this.setState({mincostValidation: 'Please enter valid Min Cost with 2 digits precision.', costValidation: ''});
    } else {
      this.setState({mincostValidation: '', costValidation: ''});
    }
    newPayload.minCost = input.detail.value;
    this.setState({denylistPayload: newPayload});
  };

  setMaxCost = (input) => {
    const newPayload = Object.assign({}, this.state.denylistPayload);
    const max = input.detail.value;
    if (max && !max.match(/^\d+\.\d{0,2}$/)) {
      this.setState({maxcostValidation: 'Please enter valid Max Cost with 2 digits precision.', costValidation: ''});
    } else {
      this.setState({maxcostValidation: '', costValidation: ''});
    }
    newPayload.maxCost = input.detail.value;
    this.setState({denylistPayload: newPayload});
  };

  OnDenylistSuccess = (response) => {
    this.setState({
                    denylistLoading: false,
                    denylistPayload: defaultDenylistPayload,
                    denylistResponseState: OPERATION_STATUS.SUCESS,
                  });
  };

  OnDenylistFailure = (response) => {
    this.setState({
                    denylistErrorMessage: response,
                    denylistLoading: false, denylistResponseState: OPERATION_STATUS.FAIL,
                  });
  };

  renderDenyListSuccess = () => {
    let type = '';
    let header = '';
    let content = '';
    let flag = true;
    if (this.state.denylistResponseState === OPERATION_STATUS.SUCESS) {
      type = 'success';
      header = 'DenyList Successful';
      content
        = `Added Inventory costs to DenyList successfully. It will expire in ${ this.state.expiryDaysDenylist } days and will take at least 24-48 hours to take effect.`;
    } else if (this.state.denylistResponseState === OPERATION_STATUS.FAIL) {
      type = 'warning';
      header = 'DenyList Failed';
      content = this.state.denylistErrorMessage;
    } else if (this.state.ttValidation !== '' || this.state.approverAliasValidation !== '' || this.state.mincostValidation !== ''
      || this.state.maxcostValidation !== '' || this.state.costValidation !== '') {
      type = 'warning';
      header = 'Input Validation Failed';
      content = content
        = `${ this.state.ttValidation }\n${ this.state.approverAliasValidation }\n${ this.state.mincostValidation }\n${ this.state.maxcostValidation }\n${ this.state.costValidation }\n`;
    } else {
      flag = false;
    }
    if (flag) {
      return (
        <SelfServiceAlert alertType={ type } alertHeader={ header } alertContent={ content } />
      );
    }
  };

  denylistButtonDisabled = () => {
    return !this.state.accessDenylistButton || !this.state.denylistPayload.iog ||
      !this.state.denylistPayload.currency ||
      !this.state.denylistPayload.tt ||
      !this.state.denylistPayload.approverAlias ||
      !this.state.denylistPayload.minCost ||
      !this.state.denylistPayload.maxCost ||
      this.state.ttValidation !== '' ||
      this.state.approverAliasValidation !== '' ||
      this.state.mincostValidation !== '' ||
      this.state.maxcostValidation !== '' ||
      this.state.costValidation !== '';
  };

  addAllowlist = () => {
    this.setState({allowlistLoading: true});
    const newPayload = Object.assign({}, this.state.allowlistPayload);
    newPayload.asin = this.state.parametersPayload.asin;
    newPayload.marketplaceId = this.state.parametersPayload.marketplaceId;
    newPayload.merchantCustomerId = this.state.parametersPayload.merchantCustomerId;
    newPayload.userAlias = this.props.identity.username;
    this.setState({allowlistPayload: newPayload});
    this.selfServiceClient.postAllowList(this.OnAllowlistSuccess, this.OnAllowlistFailure, this.state.allowlistPayload);
  };

  setExpirationDaysForAllowlist = (chosen) => {
    const newPayload = Object.assign({}, this.state.allowlistPayload);
    newPayload.daysToExpire = chosen.detail.selectedOption.value;
    this.state.expiryDaysAllowlist = chosen.detail.selectedOption.value;
    this.setState({allowlistPayload: newPayload});
  };

  setAllowlistTT = (input) => {
    const newPayload = Object.assign({}, this.state.allowlistPayload);
    const tt = input.detail.value;
    if (this.checkTT(tt)) {
      newPayload.tt = input.detail.value;
      this.setState({allowlistPayload: newPayload});
    }
  };

  setAllowlistPayload = (input) => {
    const newPayload = Object.assign({}, this.state.allowlistPayload);
    const appAlias = input.detail.value;
    if (this.checkApproverAlias(appAlias)) {
      newPayload.approverAlias = input.detail.value;
      this.setState({allowlistPayload: newPayload});
    }
  };

  setVendorCode = (input) => {
    const newPayload = Object.assign({}, this.state.allowlistPayload);
    const vendorCode = input.detail.value;
    if (vendorCode != '' && !vendorCode.match(/^[a-zA-Z0-9]+$/)) {
      this.setState({vendorCodeValidation: 'Please enter valid Vendor Code.'});
    } else {
      this.setState({vendorCodeValidation: ''});
    }
    newPayload.vendorCode = vendorCode;
    this.setState({allowlistPayload: newPayload});
  };

  OnAllowlistSuccess = (response) => {
    this.setState({
                    allowlistLoading: false,
                    allowlistPayload: defaultAllowlistPayload,
                    allowlistResponseState: OPERATION_STATUS.SUCESS,
                  });
  };

  OnAllowlistFailure = (response) => {
    this.setState({allowlistLoading: false, allowlistResponseState: OPERATION_STATUS.FAIL, allowlistErrorMessage: response});
  };

  renderAllowlistSuccess = () => {
    let type = '';
    let header = '';
    let content = '';
    let flag = true;
    if (this.state.allowlistResponseState === OPERATION_STATUS.SUCESS) {
      type = 'success';
      header = 'AllowList Successful';
      content
        = `Added Vendor code to AllowList successfully. It will expire in ${ this.state.expiryDaysAllowlist } days and will take at least 24-48 hours to take effect.`;
    } else if (this.state.allowlistResponseState === OPERATION_STATUS.FAIL) {
      type = 'warning';
      header = 'AllowList Failed';
      content = this.state.allowlistErrorMessage;
    } else if (this.state.ttValidation !== '' || this.state.approverAliasValidation !== '' || this.state.mincostValidation !== ''
      || this.state.maxcostValidation !== '' || this.state.costValidation !== '') {
      type = 'warning';
      header = 'Input Validation Failed';
      content = content = `${ this.state.ttValidation }\n${ this.state.approverAliasValidation }\n${ this.state.vendorCodeValidation }\n`;
    } else {
      flag = false;
    }

    if (flag) {
      return (
        <SelfServiceAlert alertType={ type } alertHeader={ header } alertContent={ content } />
      );
    }

  };

  allowlistButtonDisabled = () => {
    return !this.state.accessAllowlistButton || !this.state.allowlistPayload.vendorCode ||
      !this.state.allowlistPayload.tt ||
      !this.state.allowlistPayload.approverAlias ||
      !this.state.allowlistPayload.vendorCode ||
      this.state.ttValidation !== '' ||
      this.state.approverAliasValidation !== '';
  };

  renderTrace(info) {
    if (info.startsWith('[DATA]')) {
      const infoWithoutTag = info.replace('[DATA]', '');
      const dataLines = infoWithoutTag.split('\n');
      const header = dataLines.shift();

      return (<span>
        <span style={ {color: '#eee', fontWeight: 500} }> &gt; </span>
        <table className="traceTable">
          <tbody>
          <tr>
            { header.split(',').map((v, index) => (
              <th key={ index } style={ {paddingRight: '10px'} }> { v } </th>
            )) }
          </tr>
          </tbody>
          <tbody>
          { dataLines.map((row, index) => (
            <tr key={ index }>
              { row.split(',').map((v, index) => (
                <td key={ index }> { v.includes(' ') ? this.formatMonetaryValue(v) : this.formatComponentName(v) } </td>
              )) }
            </tr>
          )) }
          </tbody>
        </table>
      </span>);
    }
    if (this.state.lastTrace === info) {
      return;
    }
    this.state.lastTrace = info;
    return (<span><span style={ {color: '#eee', fontWeight: 500} }> &gt; </span> { info } </span>);
  }

  navigation = () => (
    <div data-navigation-level="0">
      <div className="website-navigation-title awsui-util-font-size-3" style={ NavigationHeaderStyle }>
        <b>
          SpotLight
        </b>
        <div className="spotlightBetaBadge">
          <Badge color={ 'blue' } content={ 'Beta' } />
        </div>
      </div>
      <SpotlightAttributes
        loadType={ this.props.match.params.type }
        loadMarketplaceId={ this.props.match.params.marketplaceId }
        loadMerchantId={ this.props.match.params.merchantId }
        loadAsin={ this.props.match.params.asin }
        callback={ this.getCallbackPayload }
        identity={ this.props.identity }
        featureList={ this.props.featureList }
        resetFunction={ this.resetTable }
        submitForm={ this.submitForm }
        loading={ this.state.loading }
      />
    </div>
  );

  render() {
    return (
      <div>
        <AppLayout
          className="awsui-util-no-gutters"
          content={ this.content() }
          navigation={ this.navigation() }
          toolsHide={ true }
        />
        <div id="f">
          <div id="console-nav-footer" className="Footer">
            Highly Confidential Data
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/spotlight/spotlight_main.jsx